define([
	"dojo/_base/array",
	"dojo/_base/lang",
	"csi",
	"csi/_base/modules",
	"csi/access/crews",
	"csi/action",
	"csi/api",
	"csi/canvas/storeFactory",
	"csi/card/_base",
	"csi/config",
	"csi/config/preferences",
	"csi/def/Action",
	"csi/locale",
	"csi/core/Key",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/middleware/rollup/rollupService",
	"csi/state/rootStore",
	"csi/types",
	"csi/view",
	"csi/xml",
	"lodash",
	"./NewFromPartMaterialAction",
	"SpecificationDataSheet/Core/util"
], function(array, lang, csi, modules, crews, csiAction, api, storeFactory, csiCard, config, configPreferences, Action, locale, Key, reflection, reflectionHints, {validateValue}, {featureTogglesStore}, types, csiView, xml, {reject}, NewFromPartMaterialAction, util) {

	const CARE_AND_COMPOSITION_REVISION = "CareAndCompositionRevision";
	const SPECIFICATION_DATA_SHEET_ITEM = "SpecificationDataSheetItem";
	const SPECIFICATION_DATA_SHEET_REVISION = "SpecificationDataSheetRevision";
	const APPAREL_BOM = "ApparelBOM";
	const MATERIAL_COMPOSITION = "MaterialComposition";
	const MATERIAL = "Material";
	const COMPOSITION = "Composition";
	const PURCHASED_ORDER = "PurchasedOrder";

	storeFactory.add(SPECIFICATION_DATA_SHEET_REVISION, SPECIFICATION_DATA_SHEET_ITEM, "Child:Items");

	storeFactory.add(CARE_AND_COMPOSITION_REVISION, APPAREL_BOM, "Child:__Parent__/Child:BOM");
	storeFactory.add(CARE_AND_COMPOSITION_REVISION, MATERIAL_COMPOSITION, "Child:Items/Child:CareAndCompositionLines/Child:TechnicalComposition", null, null, () => locale.typeId("CareAndCompositionLineItem", "TechnicalComposition"));
	storeFactory.add(CARE_AND_COMPOSITION_REVISION, MATERIAL, "Child:Items/Child:CareAndCompositionLines/Child:MaterialFromBOM");
	storeFactory.add(CARE_AND_COMPOSITION_REVISION, COMPOSITION, "Child:Items/Child:CareAndCompositionLines/Child:TechnicalComposition/Child:Composition");
	storeFactory.add(CARE_AND_COMPOSITION_REVISION, PURCHASED_ORDER, "Child:__Parent__/Child:__Parent__/Child:PurchasedOrderProducts/Child:__Parent__");

	// register BOM and SizeChart-specific controllers for state actions
	csiAction.registerControllers("SpecificationDataSheet", {
		"ReopenPhase": "Specification/SpecificationDataSheet/ReopenPhaseController"
	});

	// register SustainabilityProfile-specific controllers for state actions
	csiAction.registerControllers("SustainabilityProfile", {
		"Abandon": "Specification/SustainabilityProfile/SustainabilityProfileStateActionController",
		"EndPhase": "Specification/SustainabilityProfile/SustainabilityProfileStateActionController",
		"MakePending": "Specification/SustainabilityProfile/SustainabilityProfileStateActionController",
		"PurgeResetRevisions": "Specification/SustainabilityProfile/SustainabilityProfileStateActionController",
	});

	// restrict links to special specification item
	api.addLinkValidation("SpecificationItemDefinition", ({Special}) => !Special);

	api.setRelationshipAccess("PartMaterial", "BOMLineBOM", true);

	api.watchAttribute("RoutingSettings", "CopyBehavior", onCopyBehaviorChanged);
	api.watchAttribute("SubRoutingSettings", "CopyBehavior", onCopyBehaviorChanged);

	api.setTypeSortAttribute("SpecificationSectionDefinition", "SortOrder");

	reflectionHints.setAccess("SpecificationDataSheetRevision", "AllSections", Boolean);

	reflectionHints.setAccess("SpecificationDataSheetItem", "Actual", function(node, nodeStart, nodeTarget, ctx) {
		// if user has no access to SpecificationDataSheetItem, then cannot edit SpecificationSectionItem or SpecificationItemDefinition;
		// this could happen if user has view access to data sheet, but admin access to library
		if (!xml.nodeHasAccess(nodeStart, xml.RIGHT_WRITE)) {
			return false;
		}

		// return true if the section is not shared
		var section = ctx.cache.getNode(node.Section);
		return !util.isSectionShared(ctx, section);
	});

	reflectionHints.setAccess("SpecificationSectionItem", "Definition", function(node, nodeStart, nodeTarget, ctx) {
		return nodeTarget && nodeTarget.Special;
	});

	reflectionHints.setLocationLayoutCallback("CareAndComposition", function(tabsView, node, revisionNode) {
		const reactFeatureFlagEnabled = featureTogglesStore.CAREANDCOMPOSITION_DATASHEET_USE_REACT_UI;
		const omitIds = reactFeatureFlagEnabled ? {"Care": true} : {"CareInstructionsAndIcons": true};
		tabsView.tabs = reject(tabsView.tabs, (tab) => omitIds[tab.m_idView]);
	});

	locale.registerDisplayNameResolver("StockOrder", locale.displayNameOrType);

	function onCopyBehaviorChanged(ac, cn, node, id) {
		if (!node.CopyBehavior || xml.enumGetText(node.CopyBehavior).indexOf("SetToDraft") == -1) {
			cn.changeAttribute("SubRoutingCopyBehavior", xml.ATTR_ENUM, xml.enumCreate("SubRoutingCopyOptions", "DoNotCopySubRoutings"));
		}
		return true;
	}

	return {
		URL_ADMIN: "centric://APPAREL/SizeChartAdmin",

		// StockOrder states
		soStateAccepted: "Accepted",
		soStateCanceled: "Canceled",
		soStateDraft: "Draft",
		soStateIssued: "Issued",
		soStateFulfilled: "Fulfilled",

		// StockOrder roles
		soAdminRoles: ["Stock Order Administrator"],
		sourcingLibraryAdminRoles: ["Sourcing Library Administrator"],
		soFulfillerRoles: ["Stock Order Fulfiller", "Stock Order Administrator"],
		soRequesterRoles: ["Stock Order Requester", "Stock Order Administrator"],

		getMaterialComposition: function(/*Node*/matAttr, /*Cache*/cache, /*String?*/languageSysID) {
			// summary: get composition string from given context (must be MaterialAttributes node) in given language
			var res = "";
			var nodes = matAttr ? cache.getNodes(matAttr.TechnicalComposition) : null;
			if (nodes) {
				array.forEach(nodes, function(node, i) {
					var comp = cache.getNode(node.Composition);
					var translation = this.getTranslation(comp, languageSysID);
					var s;

					if (types.isArabicString(translation)) { //DE29059: [TAO] Translation in Arabic is not correct in the Technical File
						s = "\u202B" + node.Percentage + "\u202C" + "\u066A " + translation;
					} else {
						s = node.Percentage + "% " + translation;
					}

					res += i ? (", " + s) : s;
				}, this);
			}

			return res;
		},

		getTranslation: function(/*Node*/node, /*String?*/languageSysID) {
			// summary: get translated value for given language
			var s;

			if (languageSysID) {
				var a = node.Languages;
				if (a && a[languageSysID]) {
					s = a[languageSysID];
				}
			}

			return s || node.$Name || "";
		},
		
		onAccessCareAndCompositionSR: function(ctx, action) {
			return ctx.getParent().Items.length>0 && featureTogglesStore.CAREANDCOMPOSITION_DATASHEET_USE_REACT_UI;
		},

		/**
		 *
		 * C8FB-424 Handles manually adding Datasheets on a BaseBOMRevision
		 */
		onAccessSelectBOMMaterialDataSheets: function(ctx, action) {
			var dataPackageSheet = ctx.getNode();
			var sheet = ctx.cache.getNode(dataPackageSheet.Sheet);
			const bomMaterials = dataPackageSheet.BOMMaterials;
			const rc = reflection.getClass(sheet.$Type);
			return rc.isDerivedFrom("BaseBOMRevision") && rc.getType() != "RecipeDataSheetRevision" && bomMaterials?.length;
		},

		onAccessSelectBOMFoodDataSheets: function(ctx, action) {
			const dataPackageSheet = ctx.getNode();
			const sheet = ctx.cache.getNode(dataPackageSheet.Sheet);
			const bomFoods = dataPackageSheet.BOMFoods;
			const rcType = reflection.getClass(sheet.$Type).getType();
			return (rcType === "FoodBOMRevision" || rcType === "RecipeDataSheetRevision") && bomFoods?.length;
		},

		onAccessSelectBOMFormulatedMaterialsDataSheets: function(ctx, action) {
			const dataPackageSheet = ctx.getNode();
			const sheet = ctx.cache.getNode(dataPackageSheet.Sheet);
			const bomFMs = dataPackageSheet.BOMFormulatedMaterials;
			const rc = reflection.getClass(sheet.$Type);
			return (rc.getType() == "RecipeDataSheetRevision" || rc.getType() == "FoodBOMRevision") && bomFMs?.length;
		},

		onAccessStockOrderIsReservation: function(/*Context*/ctx, /*Action*/action) {
			return crews.isInRoles(this.soAdminRoles, ctx.getNode());
		},

		onAccessActionNewSubRouting: function(/*Context*/ctx, /*Action*/action) {
			return crews.isInRoles(this.sourcingLibraryAdminRoles, ctx.getNode());
		},

		onAccessSizeMappingSelectAll: function(/*Context*/ctx, /*Action*/action) {
			// summary: if SizeMap is Active, do not allow editing of the mapping
			return !xml.nodeGetAttributeValue(ctx.getParent(), 'Active');
		},

		onAccessActionUpdateAttributesFromMaterial: function(/*Context*/ctx, /*Action*/action) {
			if (!modules.hasModule("Sourcing"))
				return false;

			var bomLine = ctx.getNode();
			if (xml.isValidURL(bomLine.Copy))
				return false;

			var config = ctx.cache.getPathNode(bomLine, "Child:Actual/Child:ProductType/Child:ProductionConfig");
			if (!config)
				return false;

			if (config.DefaultPlacementQuoteToHighestCost)
				return true;

			if (xml.isValidURL(bomLine.BOMLineQuote))
				return true;

			var partMaterialColorsURL = ctx.getNode().PartMaterialColors;
			var partMaterialColors = ctx.cache.getNodes(partMaterialColorsURL);
			return array.some(partMaterialColors, function(pmc) {
				if (pmc) {
					var pmcQuote = pmc.PMCQuote;
					return xml.isValidURL(pmcQuote);
				}
			});
		},

		onAccessActionCreateStockOrderLineItems: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return state == this.soStateDraft && crews.isInRoles(this.soRequesterRoles, ctx.getNode());
		},

		/**
		 * Handles the Placements view table row actions
		* @param {Context} ctx
		* @param {Action} action
		*/
		onAccessActionPlacementListActions: function(ctx, action) {
			const pmNode = ctx.getNode();
			const bomRevNode = ctx.getParent();
			const bomNode = (bomRevNode != null) ? ctx.cache.getNode(bomRevNode.__Parent__) : null;
			const productSourceNode = (bomNode != null) ? ctx.cache.getNode(bomNode.__Parent__) : null;

			const lockThemePlacementList = config.getNodeSetting(bomNode, "LockThemePlacementList");
			const lockHostPlacementsForSources = config.getNodeSetting(bomNode, "LockHostPlacementsForSources");

			const actionId = action.getId();
			const isCopy = actionId === "Copy";
			if (pmNode != null && isCopy) {
				const hasAccessToActual = ctx.cache.getNode(pmNode.Actual);
				return !!hasAccessToActual;
			}

			if (pmNode != null && productSourceNode?.$Type === "ProductSource") {
				if (pmNode.LinkedDSLineItemSysID !== "" && !isCopy && actionId !== "DataSheetComment") {
					 return !lockHostPlacementsForSources;
				}
				return true;
			}
			return (pmNode != null && !xml.isValidURL(pmNode.FromMaterialPlanPlacement)) || (!lockThemePlacementList);
		},

		onAccessActionStockOrderToAccepted: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return state == this.soStateIssued && crews.isInRoles(this.soFulfillerRoles, ctx.getNode());
		},

		onAccessActionStockOrderToDraft: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return (state == this.soStateAccepted && crews.isInRoles(this.soFulfillerRoles, ctx.getNode()))
				|| (state == this.soStateIssued && crews.isInRoles(this.soRequesterRoles, ctx.getNode()));
		},

		onAccessActionStockOrderToCanceled: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return state == this.soStateIssued && crews.isInRoles(this.soRequesterRoles, ctx.getNode());
		},

		onAccessActionStockOrderToFulfilled: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return state == this.soStateAccepted && crews.isInRoles(this.soFulfillerRoles, ctx.getNode());
		},

		onAccessActionStockOrderToIssued: function(/*Context*/ctx, /*Action*/action) {
			const state = this._getStockOrderState(ctx.getNode());
			return state == this.soStateDraft && crews.isInRoles(this.soRequesterRoles, ctx.getNode());
		},

		onAccessActionCareAndCompositionDeleteComposition: function(/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			if (Object.keys(node.CompositionLines).length) {
				return true;
			}
		},

		/** the Compare action is NOT allowed in the TDS tab */
		onAccessActionCompareBOM(ctx, action) {
			if (ctx.view.prefViewContext === "TDS") {
				return false;
			}

			return true;
		},

		postActionDataSheet: function(ac) {
			const viewParent = ac.widget.viewParent;
			const view = viewParent ? viewParent.getChildView("Properties") : null;
			if (view) {
				view.cacheView.put(ac.getNode());
				view.refreshWidget();
			}

			const docsView = csiView.getView("Documents");
			if (docsView) {
				csiAction.invoke(docsView, docsView.domNode, lang.mixin(new Action(), {
					classController: "csi.view.RefreshController",
				}));
			}
		},

		refreshDataPackageSheetsTable: function(ac) {
			this._refreshTable(ac, "DataSheets", false);
		},

		/**
		 * If the child view exist, refresh the child view
		 * @param {string} childViewId
		 */
		refreshDataPackageSheetsView: function() {
			const childView = csiView.getView("DataSheets") || csiView.getView("DataSheetsInAssortment");
			if (childView) {
				childView.redraw();
				childView.set("disabled", false);
			}
		},

		refreshDocumentsTable: function(ac) {
			this._refreshTable(ac, "Documents", true);
		},

		_refreshTable: function(ac, id, bFetchNewRows) {
			var view = csiView.getView(id);
			if (view) {
				view.cacheView.cacheResponse(ac.m_response);
				if (bFetchNewRows)
					view.refreshWidget();
				else
					view.refreshDataRows();
			}
		},

		_getStockOrderState: function(stockOrder) {
			return stockOrder ? xml.enumGetText(stockOrder.State) : "";
		}
	};
});
